import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/ApiService';
import Navigation from '../components/Navigation';
import Avatar from '../components/Avatar';
import analytics from '../services/AnalyticsService';
import './Leaderboards.css';

interface Hashtag {
  id: number;
  name: string;
  displayName: string;
  category: string | null;
  userCount: number;
  isOfficial: boolean;
  recentAdds?: number;
  trendScore?: number;
}

interface LeaderboardUser {
  userId: number;
  name: string;
  photoUrl?: string;
  location?: string;
  hashtagCount?: number;
  topHashtags?: string[];
  likes: number;
  matches: number;
  influenceScore?: number;
  engagementScore?: number;
}

interface Category {
  category: string;
  hashtagCount: number;
  totalUsers: number;
}

const Leaderboards: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'popular' | 'trending' | 'global' | 'categories'>('trending');
  const [popularHashtags, setPopularHashtags] = useState<Hashtag[]>([]);
  const [trendingHashtags, setTrendingHashtags] = useState<Hashtag[]>([]);
  const [globalLeaderboard, setGlobalLeaderboard] = useState<LeaderboardUser[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analytics.trackPageView('leaderboards');
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [popular, trending, global, cats] = await Promise.all([
        ApiService.get('/leaderboards/popular-hashtags?limit=20'),
        ApiService.get('/leaderboards/trending-hashtags?limit=10'),
        ApiService.get('/leaderboards/global?limit=50'),
        ApiService.get('/leaderboards/categories')
      ]);

      setPopularHashtags(popular.data.hashtags);
      setTrendingHashtags(trending.data.hashtags);
      setGlobalLeaderboard(global.data.leaderboard);
      setCategories(cats.data.categories);
    } catch (error) {
      console.error('Failed to fetch leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHashtagClick = (hashtagId: number) => {
    analytics.track('leaderboard_hashtag_click', { hashtagId });
    navigate(`/hashtags/${hashtagId}`);
  };

  const getMedalEmoji = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getTrendIndicator = (recentAdds?: number) => {
    if (!recentAdds) return null;
    if (recentAdds >= 20) return <span className="trend-indicator hot">🔥 HOT</span>;
    if (recentAdds >= 10) return <span className="trend-indicator rising">📈 Rising</span>;
    if (recentAdds >= 5) return <span className="trend-indicator new">✨ New</span>;
    return null;
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="leaderboards-container">
          <div className="loading">Loading leaderboards...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="leaderboards-container">
        <div className="leaderboards-box">
          <h1>🏆 Hashtag Leaderboards</h1>
          <p className="leaderboards-subtitle">
            Discover trending hashtags and top community members
          </p>

          {/* Tab Navigation */}
          <div className="tabs">
            <button
              className={activeTab === 'trending' ? 'active' : ''}
              onClick={() => setActiveTab('trending')}
            >
              🔥 Trending
            </button>
            <button
              className={activeTab === 'popular' ? 'active' : ''}
              onClick={() => setActiveTab('popular')}
            >
              ⭐ Most Popular
            </button>
            <button
              className={activeTab === 'global' ? 'active' : ''}
              onClick={() => setActiveTab('global')}
            >
              👑 Top Users
            </button>
            <button
              className={activeTab === 'categories' ? 'active' : ''}
              onClick={() => setActiveTab('categories')}
            >
              📁 Categories
            </button>
          </div>

          {/* Trending Hashtags */}
          {activeTab === 'trending' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>🔥 Trending This Week</h2>
                <p>Most active hashtags in the last 7 days</p>
              </div>
              <div className="hashtag-grid">
                {trendingHashtags.map((hashtag, index) => (
                  <div
                    key={hashtag.id}
                    className="hashtag-card trending-card"
                    onClick={() => handleHashtagClick(hashtag.id)}
                  >
                    <div className="hashtag-rank">{getMedalEmoji(index + 1)}</div>
                    <div className="hashtag-info">
                      <h3>{hashtag.displayName}</h3>
                      {hashtag.category && (
                        <span className="hashtag-category">{hashtag.category}</span>
                      )}
                      <div className="hashtag-stats">
                        <span>👥 {hashtag.userCount.toLocaleString()} users</span>
                        <span>➕ {hashtag.recentAdds} this week</span>
                      </div>
                      {getTrendIndicator(hashtag.recentAdds)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Popular Hashtags */}
          {activeTab === 'popular' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>⭐ Most Popular Hashtags</h2>
                <p>Hashtags with the most users</p>
              </div>
              <div className="hashtag-list">
                {popularHashtags.map((hashtag, index) => (
                  <div
                    key={hashtag.id}
                    className="hashtag-list-item"
                    onClick={() => handleHashtagClick(hashtag.id)}
                  >
                    <div className="rank-badge">{index + 1}</div>
                    <div className="hashtag-details">
                      <div className="hashtag-name">
                        {hashtag.displayName}
                        {hashtag.isOfficial && <span className="official-badge">✓ Official</span>}
                      </div>
                      {hashtag.category && (
                        <div className="hashtag-meta">{hashtag.category}</div>
                      )}
                    </div>
                    <div className="user-count">
                      <span className="count-number">{hashtag.userCount.toLocaleString()}</span>
                      <span className="count-label">users</span>
                    </div>
                    <button className="view-btn">View →</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Global Leaderboard */}
          {activeTab === 'global' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>👑 Most Influential Users</h2>
                <p>Top community members across all hashtags</p>
              </div>
              <div className="user-leaderboard">
                {globalLeaderboard.map((user, index) => (
                  <div key={user.userId} className="leaderboard-user-card">
                    <div className="user-rank-section">
                      <div className={`rank-number ${index < 3 ? 'top-three' : ''}`}>
                        {getMedalEmoji(index + 1)}
                      </div>
                    </div>
                    <div className="user-avatar-section">
                      <Avatar photoUrl={user.photoUrl} name={user.name} size="large" />
                    </div>
                    <div className="user-info-section">
                      <h3>{user.name}</h3>
                      {user.location && <p className="user-location">📍 {user.location}</p>}
                      {user.topHashtags && user.topHashtags.length > 0 && (
                        <div className="user-top-hashtags">
                          {user.topHashtags.map((tag, i) => (
                            <span key={i} className="mini-hashtag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="user-stats-section">
                      <div className="stat-item">
                        <span className="stat-icon">🏷️</span>
                        <span className="stat-value">{user.hashtagCount}</span>
                        <span className="stat-label">hashtags</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-icon">💕</span>
                        <span className="stat-value">{user.matches}</span>
                        <span className="stat-label">matches</span>
                      </div>
                      <div className="stat-item influence">
                        <span className="stat-value">{user.influenceScore}</span>
                        <span className="stat-label">influence</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categories */}
          {activeTab === 'categories' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>📁 Hashtag Categories</h2>
                <p>Browse by category</p>
              </div>
              <div className="category-grid">
                {categories.map((category) => (
                  <div key={category.category} className="category-card">
                    <div className="category-icon">
                      {category.category === 'interests' && '🎨'}
                      {category.category === 'lifestyle' && '✨'}
                      {category.category === 'looking_for' && '💕'}
                      {category.category === 'identity' && '🌈'}
                      {!['interests', 'lifestyle', 'looking_for', 'identity'].includes(category.category) && '📌'}
                    </div>
                    <h3>{category.category.replace('_', ' ').toUpperCase()}</h3>
                    <div className="category-stats">
                      <div className="stat">
                        <span className="stat-number">{category.hashtagCount}</span>
                        <span className="stat-text">hashtags</span>
                      </div>
                      <div className="stat">
                        <span className="stat-number">{category.totalUsers.toLocaleString()}</span>
                        <span className="stat-text">users</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Leaderboards;
